/* eslint-disable no-unused-vars */
import { Button, Input, InputNumber, Modal, Select, Spin, message } from 'antd'
import { solarPerformanceUpdate } from 'api/pgads'
import { ErrorContext, SpinContext } from 'context'
import { monthsMap } from 'core'
import { isErrorContains } from 'core/component/JcTmpErrorDialog'
import { Label } from 'core/screen-util'
import React, { useContext, useMemo, useState } from 'react'
import { SOLAR_ADMIN_POWER_DATA } from '../core/EventActions'
import { getPermitActions } from '../core/selectors'
import { connect } from 'react-redux'
import { hasAccesPermits } from 'core/utils'

const SolarPerformanceDialog = ({
  data,
  targetInfo,
  visible,
  onHide = _ => _,
  onRetrieve,
  isNew = false,
  availableStatus = [],
  setTmpError,
  actionPermits,
}) => {
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const [insertData, setInsertData] = useState(data)

  const hasSolarPowerDataWritePermission = useMemo(
    _ =>
      hasAccesPermits({
        actionPermits: actionPermits,
        eventActions: [SOLAR_ADMIN_POWER_DATA],
        matchAny: true,
      }),
    [actionPermits]
  )

  const onSave = _ => {
    solarPerformanceUpdate(targetInfo)([insertData]).fork(setError, data => {
      if (isErrorContains(data)) {
        setTmpError(data)
      } else {
        message.info('Data saved.')
        setTmpError(data, _ => {
          onHide()
          onRetrieve()
        })
      }
    })
  }

  return (
    <Modal
      onCancel={onHide}
      visible={visible}
      footer={
        <div>
          <div>
            {hasSolarPowerDataWritePermission && (
              <Button type="primary" onClick={onSave}>
                Save{' '}
              </Button>
            )}
            <Button onClick={onHide}>Cancel</Button>
          </div>
        </div>
      }
      centered
      height={400}
      width={1200}
      title={<div>NERC Performance Data : {insertData.inverterGroupName}</div>}
    >
      <Spin spinning={spinning}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '230px  150px 230px 100px 250px 100px',
            gap: 5,
            width: 1000,
          }}
        >
          <Label>Inverter Group Availability :</Label>
          <Select
            value={insertData.invGroupAvailability}
            onChange={invGroupAvailability =>
              setInsertData({ ...insertData, invGroupAvailability })
            }
          >
            {availableStatus.map(d => (
              <Select.Option
                key={d.availabilityStatusCode}
                value={d.availabilityStatusCode}
              >
                {d.availabilityStatusName}
              </Select.Option>
            ))}
          </Select>
          <Label>Net Max Capacity (MW) :</Label>
          <InputNumber
            value={insertData.netmaxcap}
            onChange={netmaxcap => setInsertData({ ...insertData, netmaxcap })}
          />
          <Label>Period Active IHrs :</Label>
          <InputNumber
            value={insertData.activeIh}
            onChange={activeIh => setInsertData({ ...insertData, activeIh })}
          />

          <Label>Gross Actual Generation (MWh) :</Label>
          <InputNumber
            value={insertData.grossactgen}
            onChange={grossactgen =>
              setInsertData({ ...insertData, grossactgen })
            }
          />
          <Label>Net Actual Generation (MWh) :</Label>
          <InputNumber
            value={insertData.netactgen}
            onChange={netactgen => setInsertData({ ...insertData, netactgen })}
          />
          <Label>Expected Generation (MWh) :</Label>
          <InputNumber
            value={insertData.expectedGen}
            onChange={expectedGen =>
              setInsertData({ ...insertData, expectedGen })
            }
          />

          <Label>Monthly Plane of Array (MWh/m^2) :</Label>
          <InputNumber
            value={insertData.monthlyPoa}
            onChange={monthlyPoa =>
              setInsertData({ ...insertData, monthlyPoa })
            }
          />
          <Label>Performance Ratio :</Label>
          <InputNumber
            value={insertData.performanceRatio}
            onChange={performanceRatio =>
              setInsertData({ ...insertData, performanceRatio })
            }
          />
          <div />
          <div />
          <Label>Inactive Reserve IHrs :</Label>
          <InputNumber
            value={insertData.inactiveReserveIh}
            onChange={inactiveReserveIh =>
              setInsertData({ ...insertData, inactiveReserveIh })
            }
          />

          <Label>Mothballed IHrs :</Label>
          <InputNumber
            value={insertData.mothballedIh}
            onChange={mothballedIh =>
              setInsertData({ ...insertData, mothballedIh })
            }
          />

          <Label>Retired IHrs :</Label>
          <InputNumber
            value={insertData.retiredIh}
            onChange={retiredIh => setInsertData({ ...insertData, retiredIh })}
          />

          <div
            style={{
              gridColumnEnd: 'span 6',
              gridRowEnd: 'span 3',
              display: 'grid',
              gridTemplateColumns: '230px  150px 230px 100px 250px 100px',
              gap: 5,
              // width: 1000,
              border: '1px solid gray',
              paddingTop: '5px',
              paddingBottom: '5px',
              marginTop: '10px',
            }}
          >
            <Label>Service IHrs (Day) :</Label>
            <InputNumber
              value={insertData.serviceIhDay}
              onChange={serviceIhDay =>
                setInsertData({ ...insertData, serviceIhDay })
              }
            />
            <Label>Resource Unavailable IHrs (Day) :</Label>
            <InputNumber
              value={insertData.resourceUnaIhDay}
              onChange={resourceUnaIhDay =>
                setInsertData({ ...insertData, resourceUnaIhDay })
              }
            />
            <Label>Active Solar IHrs (Day) :</Label>
            <InputNumber
              value={insertData.activeSolarIh}
              onChange={activeSolarIh =>
                setInsertData({ ...insertData, activeSolarIh })
              }
            />
            <Label>Forced IHrs (Day) :</Label>
            <InputNumber
              value={insertData.foIhDay}
              onChange={foIhDay => setInsertData({ ...insertData, foIhDay })}
            />
            <Label>Maintenance IHrs (Day) :</Label>
            <InputNumber
              value={insertData.moIhDay}
              onChange={moIhDay => setInsertData({ ...insertData, moIhDay })}
            />
            <Label>Planned IHrs (Day) :</Label>
            <InputNumber
              value={insertData.poIhDay}
              onChange={poIhDay => setInsertData({ ...insertData, poIhDay })}
            />
          </div>

          <Label>Reserve Shutdown IHrs :</Label>
          <InputNumber
            value={insertData.rsIh}
            onChange={rsIh => setInsertData({ ...insertData, rsIh })}
          />

          <div style={{ gridColumnEnd: 'span 4' }} />
          <div
            style={{
              gridColumnEnd: 'span 6',
              gridRowEnd: 'span 3',
              display: 'grid',
              gridTemplateColumns: '230px  150px 230px 100px 250px 100px',
              gap: 5,
              // width: 1000,
              border: '1px solid gray',
              paddingTop: '5px',
              paddingBottom: '5px',
            }}
          >
            <Label>Service IHrs (Night) :</Label>
            <InputNumber
              value={insertData.serviceIhNight}
              onChange={serviceIhNight =>
                setInsertData({ ...insertData, serviceIhNight })
              }
            />
            <Label>Resource Unavailable IHrs (Night) :</Label>
            <InputNumber
              value={insertData.resourceUnaIhNight}
              onChange={resourceUnaIhNight =>
                setInsertData({ ...insertData, resourceUnaIhNight })
              }
            />
            <div />
            <div />
            <Label>Forced IHrs (Night) :</Label>
            <InputNumber
              value={insertData.foIhNight}
              onChange={foIhNight =>
                setInsertData({ ...insertData, foIhNight })
              }
            />
            <Label>Maintenance IHrs (Night) :</Label>
            <InputNumber
              value={insertData.moIhNight}
              onChange={moIhNight =>
                setInsertData({ ...insertData, moIhNight })
              }
            />
            <Label>Planned IHrs (Night) :</Label>
            <InputNumber
              value={insertData.poIhNight}
              onChange={poIhNight =>
                setInsertData({ ...insertData, poIhNight })
              }
            />
          </div>
        </div>
      </Spin>
    </Modal>
  )
}
const mapStateToProps = state => ({ actionPermits: getPermitActions(state) })

export default connect(mapStateToProps)(SolarPerformanceDialog)
